import * as React from 'react'
import { API, ICompany, ILocation, IAdminExternalSource } from '@getgreenline/homi-shared'
import { Button, Card, Empty, Row, PageHeader, message, Radio, Select } from 'antd-v4'
import { useEffect, useMemo, useState } from 'react'
import { browserHistory } from 'react-router'
import { SubscriptionCard } from './SubsciptionCard'
import SubscriptionSourceForm from './SubscriptionForm'
import { EditSubscriptionDrawer } from './EditSubsciptionDrawer'
import { ISubscription, ISubscriptionContract, WebhooksApi } from '@getgreenline/webhooks'
import { parseErrorMsg } from '../../../utilities/helpers'
import { LocalStorage } from '../../../utilities/LocalStorage'
import {
  filterAndSortSubscriptions,
  getDistinctEventTypeOptions,
  getDistinctLocationOptions,
  getDistinctIntegratorOptions,
  isOptionValuePresent,
  SubscriptionSort,
  SubscriptionStatusFilter,
} from './subscriptionHelpers'

/** Sentinel for the "All" option in the attribute filter Selects (their value can't be undefined). */
const ALL_FILTER_VALUE = '__all__'

interface Props {
  params: {
    companyId: string
  }
}

export const AdminSubscriptions: React.FC<Props> = ({ params }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [company, setCompany] = useState<ICompany | undefined>(undefined)
  const [locations, setCompanyLocations] = useState<ILocation[] | undefined>(undefined)
  const [externalSources, setExternalSources] =
    useState<IAdminExternalSource[] | undefined>(undefined)
  const [subscriptions, setSubscriptions] = useState<ISubscriptionContract[] | undefined>(undefined)
  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false)
  const [subscriptionToEdit, setSubscriptionToEdit] =
    useState<ISubscriptionContract | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState(SubscriptionStatusFilter.All)
  const [eventTypeFilter, setEventTypeFilter] = useState<string | undefined>(undefined)
  const [locationFilter, setLocationFilter] = useState<number | undefined>(undefined)
  const [integratorFilter, setIntegratorFilter] = useState<string | undefined>(undefined)
  const [sort, setSort] = useState(SubscriptionSort.EventAsc)
  const [deletingKey, setDeletingKey] = useState<string | undefined>(undefined)

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true)
        const company = await loadCompany()
        const locations = await loadLocations(company.id)
        const externalSources = await loadExternalSources()
        const subscriptions = await getSubscriptions(company.id)
        setCompany(company)
        setCompanyLocations(locations)
        setExternalSources(externalSources)
        setSubscriptions(subscriptions)
      } catch (error) {
        return message.error(parseErrorMsg(error) || 'Error loading subscriptions')
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [])

  const loadCompany = async () => {
    const companyId = parseInt(params.companyId)
    const company = await API.getCompanyById(companyId, false)
    return company
  }

  const loadLocations = async (companyId: number) => {
    const locations = await API.getLocations(companyId, false)
    return locations
  }

  const loadExternalSources = async () => {
    const externalSources = await API.getExternalSources()
    return externalSources
  }

  /** Resolves the current session's auth token, or throws so callers surface the error. */
  const requireAuthToken = async () => {
    const authToken = await LocalStorage.getAuthToken()
    if (!authToken) {
      throw new Error('Missing auth token. Please sign in again.')
    }
    return authToken
  }

  const getSubscriptions = async (companyId: number): Promise<ISubscriptionContract[]> => {
    const authToken = await requireAuthToken()
    return WebhooksApi.getSubscriptionsByCompanyId(companyId, authToken)
  }

  /**
   * Reloads the list, keeping the last known-good data on screen if the reload fails.
   * `silent` suppresses the error toast when the caller already reported the same failure —
   * otherwise one root cause (expired token, network down) surfaces two toasts.
   */
  const reloadSubscriptions = async (companyId: number, options?: { silent?: boolean }) => {
    try {
      setSubscriptions(await getSubscriptions(companyId))
    } catch (error) {
      if (!options?.silent) message.error(parseErrorMsg(error) || 'Error reloading subscriptions')
    }
  }

  const deleteSubscription = async (subscription: ISubscriptionContract) => {
    if (!company?.id) return
    setDeletingKey(subscription.combineSortKey)
    try {
      const authToken = await requireAuthToken()

      const encodedUriComponent = encodeURIComponent(subscription.combineSortKey)
      await WebhooksApi.deleteSubscription(encodedUriComponent, company.id, authToken)
      message.success('Subscription deleted')
      await reloadSubscriptions(company.id)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'Error deleting subscription')
    } finally {
      setDeletingKey(undefined)
    }
  }

  const createSubscription = async (subscription: ISubscription) => {
    try {
      const authToken = await requireAuthToken()

      await WebhooksApi.createSubscription(subscription, authToken)
      message.success('Subscription created')
      await reloadSubscriptions(subscription.companyId)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'Error creating subscription')
    }
  }

  const updateSubscription = async (subscription: ISubscription) => {
    if (!subscriptionToEdit) throw Error()
    const encodedCombineKey = encodeURIComponent(subscriptionToEdit.combineSortKey)

    try {
      const authToken = await requireAuthToken()

      await WebhooksApi.updateSubscription(subscription, encodedCombineKey, authToken)

      message.success('Subscription updated')
      await reloadSubscriptions(company!.id)
      setSubscriptionToEdit(undefined)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'Error updating subscription')
    }
  }

  const toggleSubscriptionActive = async (
    subscription: ISubscriptionContract,
    isActive: boolean,
  ) => {
    if (!company?.id) return
    let toggleFailed = false
    try {
      const authToken = await requireAuthToken()
      const encodedCombineKey = encodeURIComponent(subscription.combineSortKey)
      await WebhooksApi.setSubscriptionActive(encodedCombineKey, company.id, isActive, authToken)
    } catch (error) {
      toggleFailed = true
      message.error(parseErrorMsg(error) || 'Error updating subscription status')
    } finally {
      // Re-derive the Switch from server state whichever way the toggle went — otherwise a
      // failed toggle leaves the control visually flipped. Stay silent when the toggle already
      // reported the failure, so one root cause does not produce two toasts.
      await reloadSubscriptions(company.id, { silent: toggleFailed })
    }
  }

  const eventTypeOptions = useMemo(
    () => getDistinctEventTypeOptions(subscriptions || []),
    [subscriptions],
  )
  const locationOptions = useMemo(
    () => getDistinctLocationOptions(subscriptions || [], locations || []),
    [subscriptions, locations],
  )
  const integratorOptions = useMemo(
    () => getDistinctIntegratorOptions(subscriptions || [], externalSources || []),
    [subscriptions, externalSources],
  )

  const visibleSubscriptions = useMemo(
    () =>
      filterAndSortSubscriptions(
        subscriptions || [],
        {
          status: statusFilter,
          eventType: eventTypeFilter,
          locationId: locationFilter,
          integratorId: integratorFilter,
        },
        sort,
        locations || [],
        externalSources || [],
      ),
    [
      subscriptions,
      statusFilter,
      eventTypeFilter,
      locationFilter,
      integratorFilter,
      sort,
      locations,
      externalSources,
    ],
  )

  const hasActiveFilters =
    statusFilter !== SubscriptionStatusFilter.All ||
    eventTypeFilter !== undefined ||
    locationFilter !== undefined ||
    integratorFilter !== undefined

  const clearFilters = () => {
    setStatusFilter(SubscriptionStatusFilter.All)
    setEventTypeFilter(undefined)
    setLocationFilter(undefined)
    setIntegratorFilter(undefined)
  }

  // If the subscription backing an active filter value disappears (deleted, toggled out of
  // view), reset that filter instead of leaving it pointed at a value no longer offered.
  useEffect(() => {
    if (!isOptionValuePresent(eventTypeFilter, eventTypeOptions)) setEventTypeFilter(undefined)
    if (!isOptionValuePresent(locationFilter, locationOptions)) setLocationFilter(undefined)
    if (!isOptionValuePresent(integratorFilter, integratorOptions)) setIntegratorFilter(undefined)
  }, [eventTypeOptions, locationOptions, integratorOptions])

  return (
    <div className='p-4'>
      <PageHeader
        style={{
          border: '1px solid rgb(235, 237, 240)',
        }}
        className='mb-4'
        onBack={() => browserHistory.goBack()}
        title={company ? `${company.name} - Webhook subscriptions` : 'Loading...'}
        subTitle='View and create webhook subscriptions'
      />

      {locations && externalSources && (
        <SubscriptionSourceForm
          companyId={company?.id}
          locations={locations}
          externalSources={externalSources}
          onSubmit={createSubscription}
        />
      )}

      {isLoading ? (
        <Card loading={isLoading}></Card>
      ) : !subscriptions?.length ? (
        <Empty />
      ) : (
        <>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <h4 className='mb-0'>
              {hasActiveFilters
                ? `Subscriptions · ${visibleSubscriptions.length} of ${subscriptions?.length || 0}`
                : `Subscriptions · ${subscriptions?.length || 0} total`}
            </h4>
            {hasActiveFilters && (
              <Button type='link' onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>

          <div className='d-flex align-items-center flex-wrap mb-3'>
            <Select
              className='mr-2 mb-2'
              style={{ width: 180 }}
              value={eventTypeFilter ?? ALL_FILTER_VALUE}
              onChange={(value) =>
                setEventTypeFilter(value === ALL_FILTER_VALUE ? undefined : value)
              }
            >
              <Select.Option value={ALL_FILTER_VALUE}>Event: All</Select.Option>
              {eventTypeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>

            <Select
              className='mr-2 mb-2'
              style={{ width: 180 }}
              value={locationFilter ?? ALL_FILTER_VALUE}
              onChange={(value) =>
                setLocationFilter(value === ALL_FILTER_VALUE ? undefined : value)
              }
            >
              <Select.Option value={ALL_FILTER_VALUE}>Location: All</Select.Option>
              {locationOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>

            <Select
              className='mr-3 mb-2'
              style={{ width: 180 }}
              value={integratorFilter ?? ALL_FILTER_VALUE}
              onChange={(value) =>
                setIntegratorFilter(value === ALL_FILTER_VALUE ? undefined : value)
              }
            >
              <Select.Option value={ALL_FILTER_VALUE}>Integrator: All</Select.Option>
              {integratorOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>

            <Radio.Group
              className='mr-2 mb-2'
              value={statusFilter}
              buttonStyle='solid'
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <Radio.Button value={SubscriptionStatusFilter.All}>All</Radio.Button>
              <Radio.Button value={SubscriptionStatusFilter.Active}>Active</Radio.Button>
              <Radio.Button value={SubscriptionStatusFilter.Inactive}>Inactive</Radio.Button>
            </Radio.Group>

            <Select value={sort} className='mb-2' style={{ width: 180 }} onChange={setSort}>
              <Select.Option value={SubscriptionSort.EventAsc}>Event type A-Z</Select.Option>
              <Select.Option value={SubscriptionSort.EventDesc}>Event type Z-A</Select.Option>
              <Select.Option value={SubscriptionSort.LocationAsc}>Location A-Z</Select.Option>
              <Select.Option value={SubscriptionSort.IntegratorAsc}>Integrator A-Z</Select.Option>
            </Select>
          </div>

          {visibleSubscriptions.length === 0 ? (
            <Empty description='No subscriptions match this filter' />
          ) : (
            <Row gutter={16}>
              {externalSources &&
                locations &&
                visibleSubscriptions.map((subscription) => (
                  <SubscriptionCard
                    key={subscription.combineSortKey}
                    subscription={subscription}
                    externalSources={externalSources}
                    locations={locations}
                    isDeleting={deletingKey === subscription.combineSortKey}
                    onDelete={deleteSubscription}
                    onToggleActive={toggleSubscriptionActive}
                    setIsEditing={setEditDrawerOpen}
                    setSubscriptionToEdit={setSubscriptionToEdit}
                  />
                ))}
            </Row>
          )}
          {company?.id &&
            locations &&
            externalSources &&
            isEditDrawerOpen &&
            subscriptionToEdit && (
              <EditSubscriptionDrawer
                companyId={company?.id}
                locations={locations}
                externalSources={externalSources}
                setIsDrawerOpen={setEditDrawerOpen}
                onSubmit={updateSubscription}
                subscription={subscriptionToEdit}
              />
            )}
        </>
      )}
    </div>
  )
}
